import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { ArrowLeft, ArrowRight, LayoutGrid, Maximize, Minimize, Printer } from "lucide-react";
import "@/App.css";
import { SLIDES, TOTAL } from "@/deck/slides";
import { EASE, InstantContext } from "@/deck/shared";
import { Overview } from "@/deck/Overview";
import { AnimatedLogo } from "@/deck/AnimatedLogo";

const slideVariants = {
  enter: (d) => ({ opacity: 0, y: d >= 0 ? 48 : -48, scale: 0.99 }),
  center: { opacity: 1, y: 0, scale: 1 },
  exit: (d) => ({ opacity: 0, y: d >= 0 ? -48 : 48, scale: 0.99 }),
};

const Header = ({ dark }) => (
  <header
    className={`no-print fixed inset-x-0 top-0 z-40 transition-colors duration-700 ${
      dark ? "text-[#FAF7F2]" : "text-[#0B152C]"
    }`}
  >
    <div className="mx-auto flex h-20 w-full max-w-[1440px] items-center justify-between px-6 sm:px-12 lg:px-20">
      <div data-testid="header-logo" className="flex items-center">
        <img
          src={dark ? "/assets/villaflow-mark-light.png" : "/assets/villaflow-mark-dark.png"}
          alt="VillaFlow"
          className="h-9 w-auto sm:h-10"
        />
      </div>
      <div className="hidden items-center gap-6 sm:flex">
        <span
          className={`font-mono text-[10px] uppercase tracking-[0.3em] ${
            dark ? "text-white/50" : "text-[#0B152C]/50"
          }`}
        >
          Costiera Amalfitana
        </span>
        <span className={`h-3 w-px ${dark ? "bg-white/20" : "bg-[#0B152C]/20"}`} />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
          Investor Deck · 2026
        </span>
      </div>
    </div>
    <div
      className={`h-px w-full ${
        dark
          ? "bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent"
          : "bg-gradient-to-r from-transparent via-[#997A15]/30 to-transparent"
      }`}
    />
  </header>
);

function App() {
  const [index, setIndex] = useState(() => {
    const m = window.location.hash.match(/slide-(\d+)/);
    const n = m ? parseInt(m[1], 10) : 1;
    return Math.min(Math.max(n - 1, 0), TOTAL - 1);
  });
  const [dir, setDir] = useState(1);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const introRef = useRef(false);
  const lockRef = useRef(0);
  const touchRef = useRef(null);

  const go = useCallback((target) => {
    if (!introRef.current) return;
    setIndex((prev) => {
      const t = typeof target === "function" ? target(prev) : target;
      const c = Math.min(Math.max(t, 0), TOTAL - 1);
      setDir(c >= prev ? 1 : -1);
      return c;
    });
  }, []);
  const next = useCallback(() => go((p) => p + 1), [go]);
  const prev = useCallback(() => go((p) => p - 1), [go]);

  useEffect(() => {
    const t = setTimeout(() => {
      introRef.current = true;
      setIntroDone(true);
    }, 2600);
    return () => clearTimeout(t);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      document.documentElement.requestFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape") {
        setOverviewOpen(false);
        return;
      }
      if (overviewOpen) return;
      if (["ArrowRight", "ArrowDown", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        next();
      } else if (["ArrowLeft", "ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        prev();
      } else if (e.key === "Home") {
        go(0);
      } else if (e.key === "End") {
        go(TOTAL - 1);
      } else if (e.key === "f" || e.key === "F") {
        toggleFullscreen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, go, overviewOpen, toggleFullscreen]);

  useEffect(() => {
    const onWheel = (e) => {
      if (overviewOpen) return;
      const els = document.querySelectorAll('[data-slide-scroll="true"]');
      const el = els[els.length - 1];
      if (el && el.scrollHeight > el.clientHeight + 4) {
        if (e.deltaY > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 4) return;
        if (e.deltaY < 0 && el.scrollTop > 4) return;
      }
      if (Math.abs(e.deltaY) < 20) return;
      const now = Date.now();
      if (now - lockRef.current < 1150) return;
      lockRef.current = now;
      if (e.deltaY > 0) {
        next();
      } else {
        prev();
      }
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => window.removeEventListener("wheel", onWheel);
  }, [next, prev, overviewOpen]);

  useEffect(() => {
    window.history.replaceState(null, "", `#slide-${index + 1}`);
  }, [index]);

  useEffect(() => {
    const onHash = () => {
      const m = window.location.hash.match(/slide-(\d+)/);
      if (m) go(parseInt(m[1], 10) - 1);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [go]);

  useEffect(() => {
    if (printing) return undefined;
    const els = document.querySelectorAll('[data-slide-scroll="true"]');
    const el = els[els.length - 1];
    if (!el || el.scrollHeight <= el.clientHeight + 4) return undefined;
    const lenis = new Lenis({ wrapper: el, content: el.firstElementChild, duration: 1.05 });
    let raf = 0;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [index, printing]);

  useEffect(() => {
    if (!printing) return undefined;
    const t = setTimeout(() => window.print(), 500);
    return () => clearTimeout(t);
  }, [printing]);

  useEffect(() => {
    const after = () => setPrinting(false);
    window.addEventListener("afterprint", after);
    return () => window.removeEventListener("afterprint", after);
  }, []);

  const onTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const onTouchEnd = (e) => {
    const s = touchRef.current;
    touchRef.current = null;
    if (!s || overviewOpen) return;
    const dx = e.changedTouches[0].clientX - s.x;
    const dy = e.changedTouches[0].clientY - s.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
      if (Math.abs(dx) > 64) {
        if (dx < 0) {
          next();
        } else {
          prev();
        }
      }
      return;
    }
    if (Math.abs(dy) > 96) {
      const els = document.querySelectorAll('[data-slide-scroll="true"]');
      const el = els[els.length - 1];
      if (el && el.scrollHeight > el.clientHeight + 4) {
        if (dy < 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 4) return;
        if (dy > 0 && el.scrollTop > 4) return;
      }
      if (dy < 0) {
        next();
      } else {
        prev();
      }
    }
  };

  if (printing) {
    return (
      <InstantContext.Provider value>
        <div className="print-deck">
          {SLIDES.map((s) => {
            const C = s.Component;
            return <C key={s.id} />;
          })}
        </div>
      </InstantContext.Provider>
    );
  }

  const dark = SLIDES[index].theme === "navy";
  const ActiveSlide = SLIDES[index].Component;

  return (
    <InstantContext.Provider value={false}>
      <div
        className="relative h-screen w-screen overflow-hidden bg-[#060B19]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <motion.div
          className="no-print fixed left-0 top-0 z-50 h-[2px] bg-[#D4AF37]"
          animate={{ width: `${((index + 1) / TOTAL) * 100}%` }}
          transition={{ duration: 0.6, ease: EASE }}
        />

        <Header dark={dark} />

        <AnimatePresence>
          {!introDone && (
            <motion.div
              key="intro"
              data-testid="intro-overlay"
              className="fixed inset-0 z-[70] flex items-center justify-center bg-[#060B19]"
              exit={{ opacity: 0, scale: 1.04 }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <AnimatedLogo />
            </motion.div>
          )}
        </AnimatePresence>

        {introDone && (
          <AnimatePresence initial={false} custom={dir}>
            <motion.div
              key={index}
              custom={dir}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.65, ease: EASE }}
              className="absolute inset-0"
            >
              <ActiveSlide />
            </motion.div>
          </AnimatePresence>
        )}

        <nav
          className="no-print fixed right-5 top-1/2 z-40 hidden -translate-y-1/2 flex-col items-center gap-2.5 lg:flex"
          aria-label="Navigazione slide"
        >
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              data-testid={`dot-${s.id}`}
              onClick={() => go(i)}
              aria-label={`Vai a ${s.nav}`}
              className="group flex items-center justify-center p-1"
            >
              <span
                className={`block rounded-full transition-all duration-500 ${
                  i === index
                    ? "h-5 w-[5px] bg-[#D4AF37]"
                    : `h-[5px] w-[5px] ${
                        dark ? "bg-white/25 group-hover:bg-white/60" : "bg-[#0B152C]/25 group-hover:bg-[#0B152C]/60"
                      }`
                }`}
              />
            </button>
          ))}
        </nav>

        <div className="no-print pointer-events-none fixed inset-x-0 bottom-5 z-40 flex justify-center">
          <div
            data-testid="slide-navigation-bar"
            className="pointer-events-auto flex items-center gap-1 rounded-full border border-[#D4AF37]/20 bg-[#0B152C]/85 px-2 py-1.5 text-[#FAF7F2] shadow-2xl shadow-black/40 backdrop-blur-xl"
          >
            <button
              data-testid="slide-prev-button"
              onClick={prev}
              disabled={index === 0}
              aria-label="Slide precedente"
              className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-[#D4AF37] disabled:opacity-30"
            >
              <ArrowLeft size={15} />
            </button>
            <span
              data-testid="slide-indicator"
              className="tnum w-[78px] whitespace-nowrap text-center font-mono text-[11px] tracking-[0.18em] text-[#D4AF37]"
            >
              {String(index + 1).padStart(2, "0")} / {TOTAL}
            </span>
            <button
              data-testid="slide-next-button"
              onClick={next}
              disabled={index === TOTAL - 1}
              aria-label="Slide successiva"
              className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-[#D4AF37] disabled:opacity-30"
            >
              <ArrowRight size={15} />
            </button>
            <span className="mx-1 h-4 w-px bg-white/15" />
            <button
              data-testid="overview-modal-toggle"
              onClick={() => setOverviewOpen(true)}
              aria-label="Indice del deck"
              className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-[#D4AF37]"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              data-testid="print-deck-button"
              onClick={() => setPrinting(true)}
              aria-label="Esporta in PDF"
              className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-[#D4AF37]"
            >
              <Printer size={15} />
            </button>
            <button
              data-testid="fullscreen-toggle-button"
              onClick={toggleFullscreen}
              aria-label="Modalità presentazione"
              className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-[#D4AF37]"
            >
              {isFullscreen ? <Minimize size={15} /> : <Maximize size={15} />}
            </button>
          </div>
        </div>

        <Overview
          open={overviewOpen}
          index={index}
          onClose={() => setOverviewOpen(false)}
          onSelect={(i) => {
            go(i);
            setOverviewOpen(false);
          }}
        />
      </div>
    </InstantContext.Provider>
  );
}

export default App;
